import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import CsvDownloader from 'react-csv-downloader';

import "./cdeDetails.css";

import { getCdeDetails } from '../../api/searchApi';
import BodyHeader from '../Layouts/BodyHeader/BodyHeader';
import ViewHeader from '../Layouts/ViewHeader/ViewHeader';
import Spinner from '../Layouts/Spinner/spinner';
import getCurrentDateTime from '../utils/getCurrentDateTime';
import { cde_columns } from "../utils/columns";
import sanitizeCsv from "../utils/sanitizeCsv";
import Pagination from "../Layouts/ResultsTable/pagination";

const CdeDetails = () => {
  let { reportType } = useParams();
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [crfcdeId, setCrfcdeId] = useState(null);
  const [domain, setDomain] = useState(null);
  const [subDomain, setSubDomain] = useState(null);
  const [crfName, setCrfName] = useState(null);
  const [diseaseId, setDiseaseId] = useState(null);

  useEffect(() => {
    const handleMessage = (event) => {
      // validate the origin to prevent XSS attacks
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data && event.data.message && event.data.message === 'cde-details') {
        console.log(event.data);

        //set sessionStorage for refresh support
        window.sessionStorage.setItem('cde-details', JSON.stringify(event.data));

        if (event.data.crfcdeId)
          setCrfcdeId(event.data.crfcdeId);
        if (event.data.domain)
          setDomain(event.data.domain);
        if (event.data.subDomain)
          setSubDomain(event.data.subDomain);
        if (event.data.crfName)
          setCrfName(event.data.crfName);
        if (event.data.diseaseId)
          setDiseaseId(event.data.diseaseId);
      }
    };

    window.addEventListener("message", handleMessage);

    if (sessionStorage.getItem('cde-details')) {
      const details = JSON.parse(sessionStorage.getItem('cde-details'));

      if (details.crfcdeId)
        setCrfcdeId(details.crfcdeId);
      if (details.domain)
        setDomain(details.domain);
      if (details.subDomain)
        setSubDomain(details.subDomain);
      if (details.crfName)
        setCrfName(details.crfName);
      if (details.diseaseId)
        setDiseaseId(details.diseaseId);
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    const doSearch = async () => {
      setIsSearching(true);

      // console.log(crfcdeId);
      let results = await getCdeDetails(reportType, crfcdeId, diseaseId);
      // console.log(results);
      setSearchResults(results);
      setIsSearching(false);
    };

    if (crfcdeId && diseaseId)
      doSearch();
  }, [crfcdeId, reportType, diseaseId]);

  const totalPages = useMemo(() => searchResults ? Math.ceil(searchResults.length / pageSize) : 0, [searchResults, pageSize]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  }, []);

  const paginatedData = useMemo(() => searchResults ? searchResults.slice((currentPage - 1) * pageSize, currentPage * pageSize) : null, [searchResults, currentPage, pageSize]);

  const csvSanitizedResults = useMemo(() => {
    return sanitizeCsv(searchResults);
  }, [searchResults]);

  return (
    <>
      <BodyHeader pageHeader="CDE Detailed Report" />
      <ViewHeader>
        {domain && (
          <>
            <strong>Domain:</strong> {domain}
            <br />
          </>
        )}
        {subDomain && (
          <>
            <strong>Subdomain Name:</strong> {subDomain}
            <br />
          </>
        )}
        {crfName && (
          <>
            <strong>CRF:</strong> {crfName}
            <br />
          </>
        )}
      </ViewHeader>
      {isSearching && <Spinner />}
      {searchResults && (
        <>
          {searchResults.length > 0 &&
            <CsvDownloader
              style={{ marginTop: '20px' }}
              text="Download the CSV dataset"
              filename={`cde-details_${getCurrentDateTime()}`}
              datas={csvSanitizedResults}
              columns={cde_columns}
              wrapColumnChar={'"'}
            />
          }
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          <div className="cde-details-table">
            <table>
              <thead>
                <tr>
                  {cde_columns.map((c) => (
                    <th key={c.id}>{c.displayName}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((r) => (
                  <tr key={[r.cdeId, r.crfId, r.diseaseId, r.subDiseaseId].join('_')}>
                    <td>{r.cdeId}</td>
                    <td>{r.cdeName}</td>
                    <td>{r.suggestedName}</td>
                    <td>{r.definition}</td>
                    <td>{r.shortDescription}</td>
                    <td>{r.additionalNotes}</td>
                    <td>{r.permissibleValues}</td>
                    <td>{r.pvDescriptions}</td>
                    <td>{r.dataType}</td>
                    <td>{r.diseaseSpecificInstructions}</td>
                    <td>{r.diseaseSpecificReference}</td>
                    <td>{r.population}</td>
                    <td>{r.classification}</td>
                    <td>{r.version}</td>
                    <td>{r.versionDate}</td>
                    <td>{r.crfName}</td>
                    <td>{r.subDomainName}</td>
                    <td>{r.domainName}</td>
                    <td>{r.size}</td>
                    <td>{r.inputRestrictions}</td>
                    <td>{r.minValue}</td>
                    <td>{r.maxValue}</td>
                    <td>{r.measurementType}</td>
                    <td>{r.externalID_LOINC}</td>
                    <td>{r.externalID_SNOMED}</td>
                    <td>{r.externalID_caDSR}</td>
                    <td>{r.externalID_CDISC}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          {searchResults.length > 0 &&
            <CsvDownloader
              text="Download the CSV dataset"
              filename={`cde-details_${getCurrentDateTime()}`}
              datas={csvSanitizedResults}
              columns={cde_columns}
              wrapColumnChar={'"'}
            />
          }
        </>
      )}
    </>
  );
};

export default CdeDetails;
